import { DashboardHeader } from '@/features/dashboard/components/DashboardHeader'
import { TileRenderer } from '@/features/dashboard/components/TileRenderer'
import { SortableBentoGrid } from '@/shared/components/SortableBentoGrid'
import { SortableBentoItem } from '@/shared/components/SortableBentoItem'
import { SavingIndicator } from '@/shared/components/SavingIndicator'
import { useDashboardLayout } from '@/features/dashboard/hooks/useDashboardLayout'
import { useFocusMode } from '@/shared/hooks/useFocusMode'
import { focusDim } from '@/shared/lib/focusMode'
import { useAuth } from '@/shared/hooks/useAuth'
import { TILE_METADATA, canRoleSeeTile } from '@/features/layout/constants/tileMetadata'

// Component name of the Deep Work tile (see TileRenderer's COMPONENT_MAP).
// This is the one tile Focus Mode never dims — it's the session anchor.
const FOCUS_MODE_ANCHOR_TILE = 'DeepWork'

export function Home() {
  const { tiles, isLoading, isSaving, reorderTiles } = useDashboardLayout()
  const { isFocusModeActive } = useFocusMode()
  const { role } = useAuth()

  // Role gating is UX here, not security (backend enforces access on every
  // privileged endpoint — see #24/#34). This just keeps a member from
  // rendering a dev-only/beta tile that's sitting in their persisted layout
  // (prior role, seeded default, hand-edited layout, etc). Uses the same
  // access matrix as SettingsDashboard so the two surfaces can't drift.
  const visibleTiles = tiles.filter(t => {
    if (t.visible === false) return false
    const status = TILE_METADATA[t.component]?.status ?? 'dev-only'
    return canRoleSeeTile(status, role)
  })

  const handleReorder = (newOrder: string[]) => {
    const reorderedTiles = newOrder.map(id => tiles.find(t => t.id === id)!).filter(Boolean)
    reorderTiles(reorderedTiles)
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-canvas text-foreground flex items-center justify-center">
        <div className="text-white/60">Loading dashboard...</div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto overscroll-contain p-4 md:p-8 pb-32 md:pb-8">
      <DashboardHeader title="Overview" subtitle="Today's Focus" />

      {/* Layout controls */}
      {!isLoading && (
        <div className="mb-4 flex items-center justify-between">
          <span className="text-xs text-white/40">Drag cards to reorder</span>
          <SavingIndicator isSaving={isSaving} />
        </div>
      )}

      <SortableBentoGrid
        items={visibleTiles.map(t => t.id)}
        onReorder={handleReorder}
      >
        {visibleTiles.map(tile => (
          <SortableBentoItem
            key={tile.id}
            id={tile.id}
            colSpan={tile.colSpan}
            rowSpan={tile.rowSpan}
            // Focus Mode (GitHub issue #20): every tile except the Deep
            // Work anchor dims while a session is active, reveals on hover.
            className={focusDim({
              dimmed: isFocusModeActive && tile.component !== FOCUS_MODE_ANCHOR_TILE,
            })}
          >
            <TileRenderer component={tile.component} />
          </SortableBentoItem>
        ))}
      </SortableBentoGrid>
    </div>
  )
}
